import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, GraduationCap, Home, LogOut, Menu, X } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { FeatureNotEnabled } from '@/components/FeatureNotEnabled';
import {
  STUDENT_NAV,
  disabledNavOptionAt,
  isNavOptionAvailable,
  navKeyFromPath,
  navLabel,
} from '@/constants/navigation';
import { Logo } from '@/components/ui/Logo';
import { SecureImage } from '@/components/ui/SecureImage';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { instituteApi } from '@/features/institute/api/institute.api';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useRefreshKey } from '@/hooks/useRefreshKey';
import { cn, getNameInitials } from '@/lib/utils';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { studentApi } from '../api/student.api';
import { studentNotificationsApi } from '../api/notifications.api';
import { AcademicOverview } from '../components/AcademicOverview';
import { ClassTimetable } from '@/components/timetable/ClassTimetable';
import { NoticesView } from '@/components/notices/NoticesView';
import { AcademicCalendarView } from '@/features/institute/calendar/components/AcademicCalendarView';
import { HomeworkTasks } from '../components/HomeworkTasks';
import { StudentLeaveRequests } from '../components/StudentLeaveRequests';
import { RecordedLectures } from '@/components/lectures/RecordedLectures';
import { ExamMarksReport } from '@/components/marks/ExamMarksReport';
import { StudentProfileView } from '@/components/profile/studentprofile';

import { FeeReceipts } from '@/components/fees/FeeReceipts';
import { useStudentPreferences } from '../store/preferences.store';

/** The sections of the panel. Each one has an address of its own. */
type StudentTab = (typeof STUDENT_NAV)[number]['key'];

export function StudentDashboardPage() {
  // The school's own logo for the sidebar, from the same store the other
  // panels read, so a logo uploaded in the school's settings appears here too.
  const branding = useInstituteBranding((state) => state.branding);
  const logoVersion = useInstituteBranding((state) => state.version);
  const loadBranding = useInstituteBranding((state) => state.load);
  useEffect(() => {
    void loadBranding();
  }, [loadBranding]);
  const { logout } = useAuthStore();
  const location = useLocation();
  /** What the child asked for on the Settings tab. */
  const showSubjectIcons = useStudentPreferences((state) => state.showSubjectIcons);
  const showTeacherName = useStudentPreferences((state) => state.showTeacherName);
  const showMarksPercentage = useStudentPreferences((state) => state.showMarksPercentage);
  const itemsPerPage = useStudentPreferences((state) => state.itemsPerPage);
  const navigate = useNavigate();
  const [isNavOpen, setIsNavOpen] = useState(false);

  // The address is what decides which section is open, so a reload and a shared
  // link both land where the child expects.
  // Sections for features the school has not got switched on are left off the
  // menu, and a link straight to one shows a notice instead of the section —
  // no tab is active then, so nothing behind it loads or asks the server.
  const features = useAuthStore((state) => state.user?.features);
  const featureNames = useAuthStore((state) => state.user?.featureNames);
  const tabs = STUDENT_NAV.filter((option) => isNavOptionAvailable(option, undefined, features));
  const disabledTab = disabledNavOptionAt(STUDENT_NAV, location.pathname, features);
  const activeTab = disabledTab ? null : navKeyFromPath(STUDENT_NAV, location.pathname);
  const setActiveTab = (tab: StudentTab) =>
    navigate(STUDENT_NAV.find((option) => option.key === tab)?.to ?? '/student');

  // The dashboard switched off sends the child on to the first section they
  // can open, as the staff consoles do, rather than to a notice on sign-in.
  const landingTo = disabledTab === STUDENT_NAV[0] ? tabs[0]?.to : undefined;
  useEffect(() => {
    if (landingTo) navigate(landingTo, { replace: true });
  }, [landingTo, navigate]);

  /**
   * Who the signed-in child is.
   *
   * Read once for the whole panel — the sidebar, the header badge and every
   * tab's title come from it, so they cannot disagree about which class is
   * being looked at. Resolved from the token, so there is no id here for one
   * child to read another's record.
   */
  const { data: profile } = useApiQuery(() => studentApi.me(), []);

  /** Every figure on the overview tab. Only while that tab is open. */
  const {
    data: overview,
    isLoading: isLoadingOverview,
    error: overviewError,
    refetch: refetchOverview,
  } = useApiQuery(
    () => (activeTab === 'overview' ? studentApi.overview() : Promise.resolve(null)),
    [activeTab],
  );

  /** Any day in the week the timetable shows; empty means this week. */
  const [timetableWeekOf, setTimetableWeekOf] = useState('');

  /** One week of their section's timetable, with its papers. Only while that tab is open. */
  const {
    data: timetable,
    isLoading: isLoadingTimetable,
    error: timetableError,
    refetch: refetchTimetable,
  } = useApiQuery(
    () =>
      activeTab === 'timetable'
        ? studentApi.timetable(timetableWeekOf || undefined)
        : Promise.resolve(null),
    [activeTab, timetableWeekOf],
  );

  /** The work set for their section. Only while that tab is open. */
  const {
    data: homework,
    isLoading: isLoadingHomework,
    error: homeworkError,
    refetch: refetchHomework,
  } = useApiQuery(
    () => (activeTab === 'homework' ? studentApi.homework() : Promise.resolve(null)),
    [activeTab],
  );

  /**
   * Changes on every arrival at the current tab and whenever the bell finds a
   * new notification pointing at it. Only the notices reads take it: a "new
   * notice" opened while already on that tab used to leave the list as it was,
   * because the tab itself had not changed.
   */
  const refreshKey = useRefreshKey();

  /** What their class has been told. Only while that tab is open. */
  const {
    data: notices,
    isLoading: isLoadingNotices,
    error: noticesError,
    refetch: refetchNotices,
  } = useApiQuery(
    () => (activeTab === 'notices' ? studentApi.notices() : Promise.resolve(null)),
    [activeTab, refreshKey],
  );

  /**
   * What the institute office has put up for them. Only while that tab is open.
   *
   * A second call rather than one merged endpoint: the two boards answer
   * different questions — what my class was told, and what the institute told
   * everybody — and a failure to load one must not take the other down with it.
   */
  const {
    data: noticeBoard,
    isLoading: isLoadingNoticeBoard,
    error: noticeBoardError,
    refetch: refetchNoticeBoard,
  } = useApiQuery(
    // The board is its own feature; a school with class notices but not the
    // board must not ask for it and be refused.
    () =>
      activeTab === 'notices' && features?.includes('INSTITUTE_NOTICE')
        ? studentApi.noticeBoard()
        : Promise.resolve(null),
    [activeTab, features, refreshKey],
  );

  /** The academic calendar as they are addressed by it. Only while that tab is open. */
  const {
    data: calendar,
    isLoading: isLoadingCalendar,
    error: calendarError,
    refetch: refetchCalendar,
  } = useApiQuery(
    () => (activeTab === 'calendar' ? studentApi.calendar() : Promise.resolve(null)),
    [activeTab],
  );

  /** The lessons posted to their section. Only while that tab is open. */
  const {
    data: lectures,
    isLoading: isLoadingLectures,
    error: lecturesError,
    refetch: refetchLectures,
  } = useApiQuery(
    () => (activeTab === 'lectures' ? studentApi.lectures() : Promise.resolve(null)),
    [activeTab],
  );

  /**
   * Every paper they have been marked on.
   *
   * Read by the marks screen and by the profile's academic tab — one
   * request rather than two endpoints answering the same question
   * differently (CLAUDE.md §3).
   */
  const {
    data: marks,
    isLoading: isLoadingMarks,
    error: marksError,
    refetch: refetchMarks,
  } = useApiQuery(
    () =>
      activeTab === 'marks' || activeTab === 'profile'
        ? studentApi.marks()
        : Promise.resolve(null),
    [activeTab],
  );

  /**
   * The whole of their own record — the profile screen only.
   *
   * Not folded into `me()` above: that is read on every page of the panel, and
   * this carries their address and the family's numbers (CLAUDE.md §9).
   */
  /**
   * Their fee account. Asked for only on the tab that draws it, like every
   * other read on this page — a child opening their timetable has no reason to
   * fetch a ledger.
   */
  const {
    data: fees,
    isLoading: isLoadingFees,
    error: feesError,
    refetch: refetchFees,
  } = useApiQuery(
    () => (activeTab === 'fees' ? studentApi.fees() : Promise.resolve(null)),
    [activeTab],
  );

  const {
    data: profileRecord,
    isLoading: isLoadingRecord,
    error: recordError,
    refetch: refetchRecord,
  } = useApiQuery(
    () => (activeTab === 'profile' ? studentApi.profile() : Promise.resolve(null)),
    [activeTab],
  );

  /** What the header and sidebar print before the record arrives. */
  const displayName = profile?.fullName ?? '—';
  const className = profile?.section?.displayName ?? 'No class yet';
  const admissionNumber = profile?.admissionNumber ?? '';

  return (
    <div className="min-h-screen font-sans text-ink flex antialiased bg-[#f8f8fc] dark:bg-[#111225]">
      {/* LEFT SIDEBAR NAVIGATION — never on paper. A report card printed with
          the navigation down its side is not a report card. */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 border-r border-white/10 flex flex-col justify-between transition-transform duration-200 lg:translate-x-0 w-[268px] overflow-hidden bg-[radial-gradient(circle_at_10%_76%,rgba(97,71,221,.52),transparent_24%),linear-gradient(155deg,#15123f_0%,#20175d_60%,#171144_100%)] print:hidden',
          isNavOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <img
          src="/student-overview/institute-scene.png"
          alt=""
          className="pointer-events-none absolute bottom-[116px] left-0 w-full"
        />
        <div className="custom-scrollbar relative z-10 flex flex-col gap-6 p-5 overflow-y-auto">
          {/* Logo & Header */}
          <div className="flex items-center justify-between">
            <Logo
              tone="light"
              title={profile?.institute.name ?? ''}
              mark={
                branding?.hasLogo ? (
                  <SecureImage
                    path={instituteApi.logoPath()}
                    alt=""
                    reloadKey={logoVersion}
                    className="h-full w-full object-contain p-0.5"
                    fallback={<GraduationCap className="h-[17px] w-[17px] text-ink-3" strokeWidth={1.9} />}
                  />
                ) : undefined
              }
            />
            <button
              type="button"
              onClick={() => setIsNavOpen(false)}
              className="lg:hidden text-white/70 hover:text-white p-1 cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Student Profile Card */}
          <div className="flex items-center gap-3 px-4 py-2 rounded-[18px] bg-white/[.055] border border-white/15 shadow-[inset_0_1px_0_rgba(255,255,255,.07)]">
            {/* The child's initials, with the photograph the office uploaded
                laid over them. A stock face stood in for every child without
                one, which read as somebody else's picture; the initials also
                show through while the photograph loads or if it fails. */}
            <div
              className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/[.12] text-lg font-bold text-white ring-2 ring-white/70"
              aria-hidden
            >
              {profile ? getNameInitials(profile.fullName) : ''}
              {profile?.hasPhoto && (
                <SecureImage
                  path={studentApi.photoPath()}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-white text-sm truncate">{displayName}</span>
              <span className="text-[12px] text-emerald-300 font-semibold truncate">
                {className}
              </span>
              {/* Many institutes only allot roll numbers once a class is full,
                  so a child without one is shown nothing at all rather than a
                  label with a dash after it. */}
              {profile?.rollNumber && (
                <span className="text-[12px] text-emerald-300 font-semibold truncate">
                  Roll No. #{profile.rollNumber}
                </span>
              )}
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-col gap-1.5">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.key);
                    setIsNavOpen(false);
                  }}
                  className={cn(
                    'flex items-center gap-3 text-[14px] font-bold transition-all text-left w-full cursor-pointer',
                    activeTab === 'overview'
                      ? 'px-4 py-3 rounded-[15px]'
                      : 'px-3.5 py-2.5 rounded-[12px]',
                    isActive
                      ? activeTab === 'overview'
                        ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-[0_10px_22px_rgba(24,202,190,.28)] font-extrabold'
                        : 'bg-emerald-500 text-white shadow-md font-extrabold'
                      : 'text-white/70 hover:bg-white/5 hover:text-white',
                  )}
                >
                  <Icon
                    className={cn(
                      'h-4 w-4 shrink-0',
                      isActive ? 'text-white' : 'text-emerald-400/80',
                    )}
                  />
                  <span>{navLabel(tab, featureNames)}</span>
                  {isActive && activeTab === 'overview' && (
                    <ChevronRight className="ml-auto h-4 w-4" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Footer Actions */}
        <div
          className={cn(
            'relative z-10 px-5 py-3 border-t border-white/10 flex flex-col gap-3',
            activeTab === 'overview' && 'bg-[#171144]/80 backdrop-blur-sm',
          )}
        >
          <button
            type="button"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-[12px] text-sm font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-all cursor-pointer text-left"
          >
            <LogOut className="h-4 w-4 text-rose-400 shrink-0" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Mobile Drawer Backdrop Overlay */}
      {isNavOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-xs lg:hidden transition-opacity"
          onClick={() => setIsNavOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* MAIN VIEWPORT CONTENT AREA */}
      <div className="flex-1 flex flex-col min-h-screen lg:pl-[268px] bg-[#f8f8fc] dark:bg-[#111225] print:pl-0 min-w-0 overflow-x-clip">
        {/* Sticky Opaque Header */}
        <header
          className={cn(
            'sticky top-0 z-30 flex items-center justify-between border-b border-slate-300/80 dark:border-white/10 shadow-lg print:hidden',
            activeTab === 'overview'
              ? 'bg-[#f8f8fc] dark:bg-[#111225] px-3.5 py-2.5 sm:px-5 sm:py-3 lg:px-7'
              : 'bg-white dark:bg-[#1b1d34] px-3.5 py-2.5 sm:px-6 sm:py-3.5',
          )}
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setIsNavOpen(true)}
              className="lg:hidden p-1.5 sm:p-2 rounded-field text-ink-2 hover:bg-surface-2 cursor-pointer shrink-0"
              aria-label="Open Navigation Menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            <div
              className={cn(
                'flex items-center gap-1.5 sm:gap-2 min-w-0',
                activeTab === 'overview' &&
                  'gap-2 sm:gap-3 rounded-[17px] border border-[#e5e5f0] bg-white px-3 py-2 sm:px-4 sm:py-3 shadow-[0_4px_12px_rgba(27,23,72,.06)] dark:border-white/10 dark:bg-[#1d1f39]',
              )}
            >
              {activeTab === 'overview' ? (
                <Home className="h-4 w-4 sm:h-5 sm:w-5 text-[#4285df] shrink-0" />
              ) : (
                <span className="hidden sm:block h-6 w-1.5 rounded-pill bg-emerald-500 shrink-0" />
              )}
              <h1 className="text-sm sm:text-base font-extrabold text-ink tracking-tight truncate">
                {(() => {
                  const option = STUDENT_NAV.find((entry) => entry.key === activeTab);
                  return option ? navLabel(option, featureNames) : null;
                })()}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <span
              className={cn(
                'hidden sm:inline-flex items-center gap-1.5 rounded-pill text-sm font-extrabold',
                activeTab === 'overview'
                  ? 'px-3 sm:px-4 py-1 sm:py-1 bg-[#e9fafb] text-[#188b9b] border border-[#c8eff1]'
                  : 'px-2.5 sm:px-3 py-1 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20',
              )}
            >
              <GraduationCap className="h-3.5 w-3.5" /> {className}
              {admissionNumber && ` (${admissionNumber})`}
            </span>

            <NotificationBell source={studentNotificationsApi} />

            <ThemeToggle />
          </div>
        </header>

        {/* PAGE CONTENTS - COMPONENTS (WITHOUT 'TAB' SUFFIX) */}
        <main className="flex flex-col gap-5 sm:gap-6 flex-1 w-full mx-auto p-3.5 sm:p-5 lg:p-6 max-w-full">
          {disabledTab && <FeatureNotEnabled label={navLabel(disabledTab, featureNames)} />}
          {activeTab === 'overview' && (
            <AcademicOverview
              profile={profile}
              overview={overview}
              isLoading={isLoadingOverview}
              error={overviewError}
              onRetry={refetchOverview}
              showTeacherName={showTeacherName}
            />
          )}
          {activeTab === 'timetable' && (
            <ClassTimetable
              timetable={timetable}
              isLoading={isLoadingTimetable}
              error={timetableError}
              onRetry={refetchTimetable}
              showSubjectIcons={showSubjectIcons}
              showTeacherName={showTeacherName}
              onWeekChange={setTimetableWeekOf}
            />
          )}
          {activeTab === 'homework' && (
            <HomeworkTasks
              list={homework}
              isLoading={isLoadingHomework}
              error={homeworkError}
              onRetry={refetchHomework}
            />
          )}
          {activeTab === 'notices' && (
            <NoticesView
              classNotices={notices}
              instituteNotices={noticeBoard}
              isLoading={isLoadingNotices || isLoadingNoticeBoard}
              error={noticesError || noticeBoardError}
              onRetry={() => {
                void refetchNotices();
                void refetchNoticeBoard();
              }}
              pageSize={itemsPerPage}
              showTeacherName={showTeacherName}
              hideHeader
            />
          )}
          {activeTab === 'calendar' && (
            <AcademicCalendarView
              data={calendar}
              isLoading={isLoadingCalendar}
              error={calendarError}
              onRetry={refetchCalendar}
              title="Academic calendar"
              description="Exams, events and holidays for your class this session."
              emptyDescription="Your school has not put anything on this session's calendar for students yet. Exam weeks, events and holidays appear here as soon as the school office adds them."
            />
          )}
          {activeTab === 'lectures' && (
            <RecordedLectures
              list={lectures}
              isLoading={isLoadingLectures}
              error={lecturesError}
              onRetry={refetchLectures}
              onSetSaved={studentApi.setLectureSaved}
              onMarkWatched={studentApi.markLectureWatched}
              hideHeader
              pageSize={itemsPerPage}
            />
          )}
          {activeTab === 'marks' && (
            <ExamMarksReport
              card={
                profile && {
                  instituteName: profile.institute.name,
                  academicYearName: profile.academicYear.name,
                  fullName: profile.fullName,
                  admissionNumber: profile.admissionNumber,
                  rollNumber: profile.rollNumber,
                  classDisplayName: profile.section?.displayName ?? null,
                  classTeacher: profile.classTeacher,
                }
              }
              list={marks}
              isLoading={isLoadingMarks}
              error={marksError}
              onRetry={refetchMarks}
              showPercentage={showMarksPercentage}
            />
          )}
          {activeTab === 'leave' && (
            <StudentLeaveRequests
              child={
                profile && {
                  fullName: profile.fullName,
                  className: profile.section?.displayName ?? 'No class yet',
                  rollNumber: profile.rollNumber,
                }
              }
            />
          )}
          {activeTab === 'profile' && (
            <StudentProfileView
              profile={profile}
              studentProfile={profile}
              photo={
                profile?.hasPhoto ? (
                  <SecureImage
                    path={studentApi.photoPath()}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : undefined
              }
              record={profileRecord}
              marks={marks}
              isLoading={isLoadingRecord || isLoadingMarks}
              error={recordError || marksError}
              onRetry={() => {
                refetchRecord();
                refetchMarks();
              }}
            />
          )}
          {activeTab === 'fees' && (
            <FeeReceipts
              account={fees}
              isLoading={isLoadingFees}
              error={feesError}
              onRetry={refetchFees}
            />
          )}
        </main>
      </div>
    </div>
  );
}
